'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { usdCompact, truncateMiddle } from '../lib/format'
import { useAlkanes } from '../lib/store'
import useTopTraders from '../lib/useTopTraders'
import useTwetchIdentities from '../lib/useTwetchIdentities'
import { Avatar } from './social-feed'
import UserHoverCard from './user-hover-card'

function Bar({ className }: { className?: string }) {
	return <span className={cn('block h-3 rounded bg-secondary', className)} aria-hidden="true" />
}

/// Top traders rail — REAL rows when the API serves them: the wallets with
/// the most pool-swap volume in the last 24h (valued at today's mids — a
/// ranking, not PnL), wearing their Twetch identity where one is linked and
/// opening their canonical profile on click. Falls back to the honest ghost
/// anatomy while the endpoint is unavailable. Shared by the own-profile
/// screen and the wallet profile; `className` replaces the visibility gate
/// for pages that also carry the token rail.
export function FollowRail({ className }: { className?: string }) {
	const top = useTopTraders()
	const openProfile = useAlkanes((s) => s.openProfile)
	const identities = useTwetchIdentities(top.traders.map((t) => t.address))
	const live = !top.unavailable && top.traders.length > 0

	return (
		<aside className={className ?? 'hidden xl:block'}>
			<div className="rounded-2xl bg-card p-4" data-testid="alkanes-follow-rail">
				<h3 className="text-sm font-semibold">
					Top traders <span className="text-xxs font-normal text-muted-foreground">24h</span>
				</h3>
				{live ? (
					<React.Fragment>
						<ul className="mt-3 space-y-1">
							{top.traders.slice(0, 5).map((t) => {
								const user = identities[t.address]
								return (
									<li key={t.address}>
										<UserHoverCard userId={user?.id} user={user}>
										<button
											type="button"
											onClick={() => openProfile(t.address)}
											className="flex w-full items-center gap-2.5 rounded-xl px-1.5 py-1.5 text-left transition-colors hover:bg-secondary/50"
										>
											{user ? (
												<Avatar user={user} userId={user.id} size={36} />
											) : (
												<span className="grid size-9 shrink-0 place-items-center rounded-full bg-secondary text-xxs font-semibold text-muted-foreground">
													{t.address.slice(4, 6).toUpperCase()}
												</span>
											)}
											<span className="min-w-0 flex-1">
												<span
													className={cn(
														'block truncate text-xs font-semibold',
														!user && 'font-mono font-medium'
													)}
												>
													{user?.name ?? truncateMiddle(t.address, 6, 4)}
												</span>
												<span className="block text-xxs text-muted-foreground">
													{t.trades} {t.trades === 1 ? 'trade' : 'trades'}
												</span>
											</span>
											<span className="shrink-0 text-right text-xs font-semibold tabular-nums">
												{t.volume_usd !== null ? usdCompact(t.volume_usd) : '—'}
											</span>
										</button>
										</UserHoverCard>
									</li>
								)
							})}
						</ul>
						<p className="mt-3 text-xxs leading-relaxed text-muted-foreground">
							Pool-swap volume at today’s prices. Following arrives with the social launch,
							powered by Twetch.
						</p>
					</React.Fragment>
				) : (
					<React.Fragment>
						<div aria-hidden="true" className="mt-3 space-y-3 opacity-60">
							{[0, 1, 2, 3, 4].map((i) => (
								<div key={i} className="flex items-center gap-2.5">
									<span className="size-9 shrink-0 rounded-full bg-secondary" />
									<div className="min-w-0 flex-1 space-y-1.5">
										<Bar className="w-20" />
										<Bar className="w-14" />
									</div>
									<span className="rounded-lg bg-secondary px-3 py-1 text-xs font-bold text-muted-foreground">
										Follow
									</span>
								</div>
							))}
						</div>
						<p className="mt-3 text-xxs leading-relaxed text-muted-foreground">
							The 24h trader ranking could not load — the rows above are layout only, nobody in
							them is a trader.
						</p>
					</React.Fragment>
				)}
			</div>
		</aside>
	)
}
